import React, { useEffect, useState } from 'react';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import { reportBudget } from 'loot-core/client/queries';
import { useSpreadsheet } from 'loot-core/client/SpreadsheetProvider';
import { type BudgetTemplateLine } from 'loot-core/shared/budget-template';
import { monthsOfFiscalYear, sheetForMonth } from 'loot-core/shared/months';
import { amountToCurrency } from 'loot-core/shared/util';
import { type CategoryGroupEntity } from 'loot-core/types/models';

import { SlideToggle } from '../../common/SlideToggle';
import { View } from '../../common/View';
import { type ITableTotals } from '../types';

import { getCashflowProjectionData } from './chart-data';
import {
  AggregateMode,
  chartTooltipStyle,
  Datasource,
  toggleStyle,
} from './shared';
import { useGlobalPref } from '../../../hooks/useGlobalPref';

type CustomTooltipProps = TooltipProps<number, 'date'> & {
  incomeValue: number;
};

function CustomTooltip({ payload, incomeValue }: CustomTooltipProps) {
  if (!payload || !payload[0]) {
    return <></>;
  }

  const data = payload[0].payload;
  return (
    <View style={chartTooltipStyle}>
      Name: {data.name}
      <br />
      Amount: {amountToCurrency(data.value)} <br />
      {data.name === 'Income' ? null : (
        <>Percentage: {((data.value * 100) / incomeValue).toFixed(2)}%</>
      )}
    </View>
  );
}

type ICashFlowProjectionChartProps = {
  year: number;
  budgetLines: BudgetTemplateLine[];
  categoryGroups: CategoryGroupEntity[];
  groupTotals: { [groupId: string]: ITableTotals };
  datasource: Datasource;
};

export function CashFlowProjectionChart({
  year,
  budgetLines,
  categoryGroups,
  groupTotals,
  datasource,
}: ICashFlowProjectionChartProps) {
  const [aggregateMode, setAggregateMode] = useState(
    AggregateMode.MonthlyAverage,
  );
  const [chartData, setChartData] = useState(null);
  const [fiscalYearStart] = useGlobalPref('fiscalYearStart');
  const spreadsheet = useSpreadsheet();

  useEffect(() => {
    getCashflowProjectionData(
      categoryGroups,
      aggregateMode,
      async function (catGroups: CategoryGroupEntity[]) {
        if (datasource === Datasource.BudgetTemplate) {
          const acc = { monthly: 0, yearly: 0 };
          for (const group of catGroups) {
            acc.monthly += groupTotals[group.id].monthly;
            acc.yearly += groupTotals[group.id].yearly;
          }
          return acc;
        } else if (datasource === Datasource.AnnualGrid) {
          let acc = 0;
          for (const group of catGroups) {
            for (const month of monthsOfFiscalYear(year, fiscalYearStart)) {
              acc += (
                await spreadsheet.get(
                  sheetForMonth(month.month),
                  reportBudget.groupBudgeted(group.id),
                )
              ).value;
            }
          }
          return {
            monthly: Math.floor(acc / 12),
            yearly: acc,
          };
        } else {
          throw new Error('Unknown data source');
        }
      },
    ).then(data => {
      setChartData(data);
    });
  }, [aggregateMode, year]);
  const incomeValue = chartData?.find(ch => ch.name === 'Income').value;

  return (
    <View data-adoption-id="cash-flow-projection">
      <h3>Cash Flow Projection</h3>

      <View style={toggleStyle} onClick={(e) => e.preventDefault()}>
        Monthly Average
        <SlideToggle
          onToggle={val =>
            setAggregateMode(
              val ? AggregateMode.YearlyTotal : AggregateMode.MonthlyAverage,
            )
          }
        />
        Yearly Total
      </View>

      {chartData && (
        <ResponsiveContainer height={300}>
          <BarChart layout="vertical" data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <Legend
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              payload={chartData.map(o => ({
                value: o.name,
                type: 'rect',
                color: o.color,
              }))}
            />
            <Tooltip content={<CustomTooltip incomeValue={incomeValue} />} />
            <XAxis type="number" dataKey="value" />
            <YAxis type="category" dataKey="name" hide />
            <Bar dataKey="value" fill="#8884d8">
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </View>
  );
}
